import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, FileText, Building2, Settings, LogOut,
  ChevronRight, Globe, Shield, HelpCircle, Bell, Briefcase,
  Receipt, CreditCard, BarChart3, Calculator, Package, FolderOpen
} from 'lucide-react';
import '../styles/mobile.css';

export default function MobileMore() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const changeLanguage = () => {
    const languages = ['sv', 'en', 'no', 'da', 'es'];
    const currentIndex = languages.indexOf(i18n.language);
    const nextIndex = (currentIndex + 1) % languages.length;
    i18n.changeLanguage(languages[nextIndex]);
  };

  const languageNames = {
    sv: 'Svenska',
    en: 'English',
    no: 'Norsk',
    da: 'Dansk',
    es: 'Espanol'
  };

  const mainMenuItems = [
    { icon: <Users size={20} />, label: t('customers.title'), path: '/customers', color: '#e0f2fe', iconColor: '#0284c7' },
    { icon: <Building2 size={20} />, label: t('suppliers.title'), path: '/suppliers', color: '#f3e8ff', iconColor: '#9333ea' },
    { icon: <Receipt size={20} />, label: t('bookkeeping.title'), path: '/bookkeeping', color: '#dcfce7', iconColor: '#16a34a' },
    { icon: <CreditCard size={20} />, label: t('nav.bankImport'), path: '/bank-import', color: '#fef3c7', iconColor: '#d97706' },
    { icon: <Calculator size={20} />, label: t('nav.payroll'), path: '/payroll', color: '#fee2e2', iconColor: '#dc2626' },
    { icon: <Package size={20} />, label: t('nav.inventory'), path: '/inventory', color: '#dbeafe', iconColor: '#2563eb' },
    { icon: <FolderOpen size={20} />, label: t('nav.fixedAssets'), path: '/fixed-assets', color: '#fce7f3', iconColor: '#db2777' },
    { icon: <BarChart3 size={20} />, label: t('reports.title'), path: '/reports', color: '#f0fdf4', iconColor: '#15803d' },
  ];

  const settingsItems = [
    { icon: <Globe size={20} />, label: t('language.title'), value: languageNames[i18n.language] || 'Svenska', action: changeLanguage },
    { icon: <Bell size={20} />, label: 'Notifikationer', path: '/m/notifications' },
    { icon: <Building2 size={20} />, label: t('settings.companyInfo'), path: '/settings' },
    { icon: <Shield size={20} />, label: t('team.title'), path: '/team' },
  ];

  return (
    <div className="mobile-app">
      {/* Header */}
      <header className="mobile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="mobile-header-btn" onClick={() => navigate('/m')}>
            <ArrowLeft size={20} />
          </button>
          <div className="mobile-header-title">{t('nav.settings')}</div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mobile-content">
        {/* User Card */}
        <div className="mobile-card" style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '20px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2a4d7a 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            fontWeight: 600
          }}>
            <Briefcase size={28} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: '17px' }}>Yubi.ai</div>
            <div style={{ color: '#64748b', fontSize: '14px' }}>Bokföring & Fakturering</div>
          </div>
        </div>

        {/* Main Menu */}
        <div className="mobile-section-header">
          <h3 className="mobile-section-title">Funktioner</h3>
        </div>
        <div className="mobile-list">
          {mainMenuItems.map((item, idx) => (
            <div 
              key={idx}
              className="mobile-list-item"
              onClick={() => navigate(item.path)}
            >
              <div className="mobile-list-icon" style={{ background: item.color, color: item.iconColor }}>
                {item.icon}
              </div>
              <div className="mobile-list-content">
                <div className="mobile-list-title">{item.label}</div>
              </div>
              <ChevronRight size={20} color="#94a3b8" />
            </div>
          ))}
        </div>

        {/* Settings */}
        <div className="mobile-section-header">
          <h3 className="mobile-section-title">{t('nav.settings')}</h3>
        </div>
        <div className="mobile-list">
          {settingsItems.map((item, idx) => (
            <div 
              key={idx}
              className="mobile-list-item"
              onClick={item.action || (() => navigate(item.path))}
            >
              <div className="mobile-list-icon" style={{ background: '#f1f5f9', color: '#64748b' }}>
                {item.icon}
              </div>
              <div className="mobile-list-content">
                <div className="mobile-list-title">{item.label}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {item.value && <span style={{ color: '#64748b', fontSize: '14px' }}>{item.value}</span>}
                <ChevronRight size={20} color="#94a3b8" />
              </div>
            </div>
          ))}
        </div>

        {/* Help & Support */}
        <div className="mobile-section-header">
          <h3 className="mobile-section-title">Hjälp</h3>
        </div>
        <div className="mobile-list">
          <div className="mobile-list-item" onClick={() => window.open('https://yubi.ai/help', '_blank')}>
            <div className="mobile-list-icon" style={{ background: '#f1f5f9', color: '#64748b' }}>
              <HelpCircle size={20} />
            </div>
            <div className="mobile-list-content">
              <div className="mobile-list-title">Hjälpcenter</div>
            </div>
            <ChevronRight size={20} color="#94a3b8" />
          </div>
        </div>

        {/* Logout */}
        {token && (
          <button 
            className="mobile-btn mobile-btn-outline"
            onClick={handleLogout}
            style={{ 
              marginTop: '24px', 
              color: '#dc2626', 
              borderColor: '#fee2e2',
              background: '#fee2e2'
            }}
          >
            <LogOut size={18} />
            {t('nav.logout')}
          </button>
        )}

        {/* Version */}
        <div style={{ textAlign: 'center', marginTop: '24px', color: '#94a3b8', fontSize: '12px' }}>
          Yubi.ai Mobile v1.0.0
        </div>
      </main>
    </div>
  );
}
